import React from 'react';
import { AnyAction, isRejectedWithValue, Middleware } from '@reduxjs/toolkit';
import { Snackbar } from 'components/Snackbars';
import { toast } from 'react-toastify';
import jwtDecode from 'jwt-decode';
import { toastErrorConfig } from 'config/ui/toastConfig';
import { localStorageWrapper } from '../functions/localStorage';
import { UserRoleEnum } from '../common/user/enums/user-role.enum';
import { IAccessToken } from './user/selectors';
import { DriverLogoutPage, LoginPage, LogoutPage } from '../routes/paths';

const rtkQueryErrorLogger: Middleware = () => (next) => (action: AnyAction) => {
  if (isRejectedWithValue(action)) {
    const { status = 0 } = action.payload as any;

    const payload = action.payload as Record<
      string,
      {
        status?: number;
        error?: string;
        message?: (string | { constraints: string })[];
      }
    >;

    let error: (string | { constraints: string })[] = [];

    if (payload?.status >= 500) {
      error = payload.data?.message
        ? typeof payload.data.message === 'string'
          ? [payload.data.message]
          : payload.data.message
        : ['Internal Server Error'];
    }

    if (status === 401) {
      const accessTokenString = localStorageWrapper.getAccessToken();
      const accessToken: IAccessToken | undefined = accessTokenString
        ? (jwtDecode(accessTokenString) as IAccessToken)
        : undefined;
      if (accessToken?.roles.includes(UserRoleEnum.CANDIDATE)) {
        window.location.href = DriverLogoutPage;
      } else if (window.location.pathname === LoginPage) {
        toast.error(
          <Snackbar message={payload.data.error || 'Invalid Credentials'} />,
          toastErrorConfig
        );
      } else if (accessTokenString) {
        // Only tear down an actual session. A 401 with no token is a stale,
        // in-flight request landing on a public page (e.g. the /upload flow
        // after logout cleared the token) — redirecting would bounce the
        // driver off /upload to /logout → /login. Leave the page alone.
        window.location.href = LogoutPage;
      }
    }

    if (payload.data?.error) {
      error = [payload.data.error];
      if (payload.data.message) {
        error =
          typeof payload.data.message === 'string'
            ? [payload.data.message]
            : payload.data.message;
      }
    } else if (typeof payload.error === 'string') {
      error = [payload.error];
    }

    error.forEach((error) => {
      toast.error(
        <Snackbar
          message={
            typeof error === 'string'
              ? error
              : Object.values(error.constraints)[0]
          }
        />,
        toastErrorConfig
      );
    });
  }

  return next(action);
};

export default rtkQueryErrorLogger;
